import { Component, Output, EventEmitter, Input, OnInit } from '@angular/core';
import { EditSubOrganizationService, SubOrganizationDetails, UpdateSubOrganizationRequest } from './edit-sub-organization.service';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from 'src/app/core/services/auth.service';
import { MatCardModule } from '@angular/material/card';
import { Router, ActivatedRoute } from '@angular/router';
import { OrganizationStateService } from '../../../shared/organization-state.service';

@Component({
  selector: 'app-edit-sub-organization',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatCardModule],
  templateUrl: './edit-sub-organization.component.html',
  styleUrl: './edit-sub-organization.component.scss',
})
export class EditSubOrganizationComponent implements OnInit {
  editForm: FormGroup;
  subOrganizationId: string = '';
  parentOrganizationId: string = '';
  subOrganizationData: SubOrganizationDetails = {} as SubOrganizationDetails;
  parentOrganizationData: any = {};
  departments = [];
  serverError: any;
  isLoading = false;

  @Input() subOrganizationToEdit: any;
  @Output() editSubOrgSuccess = new EventEmitter<void>();

  constructor(
    private formBuilder: FormBuilder,
    private editSubOrganizationService: EditSubOrganizationService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private organizationStateService: OrganizationStateService
  ) {
    this.editForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      address1: [''],
      address2: [''],
      country: [''],
      city: [''],
      state: [''],
      postalCode: [''],
      phone: [''],
      firstName: [''],
      lastName: [''],
      pointOfContactDetails: [''],
      active: [false] // Initialize as false, will be set from API response
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.subOrganizationId = params.get('id') || '';
      this.parentOrganizationId = params.get('parentId') || '';
      
      if (this.subOrganizationId) {
        this.loadSubOrganizationData();
      }
      
      if (this.parentOrganizationId) {
        this.loadParentOrganizationData();
      }
    });
  }

  loadSubOrganizationData() {
    this.isLoading = true;
    const token = this.authService.token;
    this.editSubOrganizationService.getSubOrganizationById(this.subOrganizationId, token).subscribe({
      next: (data) => {
        this.subOrganizationData = data;
        this.populateForm(data);
        this.isLoading = false;
      },
      error: (error) => {
        this.serverError = error;
        this.isLoading = false;
      }
    });
  }

  loadParentOrganizationData() {
    const token = this.authService.token;
    this.editSubOrganizationService.getParentOrganization(this.parentOrganizationId, token).subscribe({
      next: (data) => {
        this.parentOrganizationData = data;
      },
      error: (error) => {
        console.error('Failed to load parent organization:', error);
      }
    });
  }

  populateForm(data: SubOrganizationDetails) {
    console.log('API Response active value:', data.active); // Debug log
    
    const formData = {
      ...this.extractBasicSubOrgFields(data),
      ...this.extractSubOrgAddressFields(data),
      ...this.extractSubOrgContactFields(data)
    };
    
    this.editForm.patchValue(formData);
    console.log('Form active value after patch:', this.editForm.get('active')?.value); // Debug log
  }

  private extractBasicSubOrgFields(data: SubOrganizationDetails) {
    return {
      name: data.name || '',
      active: Boolean(data.active) // Explicitly convert to boolean
    };
  }

  private extractSubOrgAddressFields(data: SubOrganizationDetails) {
    const address = this.getAddressWithDefaults(data.address);
    return this.buildAddressFieldsObject(address);
  }

  private getAddressWithDefaults(address: any) {
    return address || {
      address1: '',
      address2: '',
      country: '',
      city: '',
      state: '',
      postalCode: '',
      phone: ''
    };
  }

  private buildAddressFieldsObject(address: any) {
    return {
      address1: address.address1 || '',
      address2: address.address2 || '',
      country: address.country || '',
      city: address.city || '',
      state: address.state || '',
      postalCode: address.postalCode || '',
      phone: address.phone || ''
    };
  }

  private extractSubOrgContactFields(data: SubOrganizationDetails) {
    const contactInfo = data.contactInfo || {
      firstName: '',
      lastName: ''
    };
    return {
      firstName: contactInfo.firstName || '',
      lastName: contactInfo.lastName || '',
      pointOfContactDetails: '' // Not available in API response - keep empty for user input
    };
  }

  onSubmit() {
    if (this.editForm.valid) {
      this.processSubOrganizationUpdate();
    } else {
      this.markFormGroupTouched();
    }
  }

  private processSubOrganizationUpdate() {
    this.isLoading = true;
    const formData = this.editForm.value;
    const updateData = this.buildSubOrganizationUpdateData(formData);
    
    const token = this.authService.token;
    this.editSubOrganizationService.updateSubOrganization(updateData, token).subscribe({
      next: (response) => {
        this.handleUpdateSuccess();
      },
      error: (error) => {
        this.handleUpdateError(error);
      }
    });
  }

  private buildSubOrganizationUpdateData(formData: any): UpdateSubOrganizationRequest {
    return {
      orgId: this.subOrganizationId,
      name: formData.name,
      parentOrganizationId: this.parentOrganizationId,
      address: this.buildSubOrgAddressUpdateData(formData),
      contactInfo: this.buildSubOrgContactUpdateData(formData),
      active: formData.active,
      excludeReferrals: this.subOrganizationData.excludeReferrals ?? true
    };
  }

  private buildSubOrgAddressUpdateData(formData: any) {
    return {
      address1: formData.address1 || '',
      address2: formData.address2 || '',
      country: formData.country || '',
      city: formData.city || '',
      state: formData.state || '',
      postalCode: formData.postalCode || '',
      phone: formData.phone || ''
    };
  }

  private buildSubOrgContactUpdateData(formData: any) {
    return {
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      pointOfContactDetails: formData.pointOfContactDetails || ''
    };
  }

  private handleUpdateSuccess() {
    this.isLoading = false;
    this.editSubOrgSuccess.emit();
    
    // Navigate immediately to show-sub-organization table with success indicator
    this.router.navigate(['/organization/show-sub-organization', this.parentOrganizationId], {
      queryParams: { updated: '1' }
    });
  }

  private handleUpdateError(error: any) {
    this.serverError = error;
    this.isLoading = false;
  }

  private markFormGroupTouched() {
    Object.keys(this.editForm.controls).forEach(key => {
      const control = this.editForm.get(key);
      control?.markAsTouched();
    });
  }

  onCancel() {
    this.router.navigate(['/organization/show-sub-organization', this.parentOrganizationId]);
  }

  getFieldError(fieldName: string): string {
    const field = this.editForm.get(fieldName);
    if (!field) {
      return '';
    }

    return this.getValidationErrorMessage(field, fieldName);
  }

  private getValidationErrorMessage(field: any, fieldName: string): string {
    const errorMessages: Record<string, () => string> = {
      required: () => `${fieldName} is required`,
      email: () => 'Please enter a valid email address',
      minlength: () => `${fieldName} must be at least ${field.errors?.['minlength'].requiredLength} characters`,
      min: () => `${fieldName} must be a positive number`
    };

    for (const errorType of Object.keys(errorMessages)) {
      if (field.hasError(errorType)) {
        return errorMessages[errorType]();
      }
    }

    return '';
  }

}